// utils/EmailSenderBase.js

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";

// db は動的にインポート（初期化前呼び出し対策）
let dbPromise = null;

const getDb = async () => {
  if (dbPromise) return dbPromise;

  dbPromise = import("../firebase").then((module) => {
    if (!module.db) {
      throw new Error("firebase.js で db が export されていません");
    }
    return module.db;
  });

  return dbPromise;
};

const DAILY_EMAIL_LIMIT = 60000;

// ★ローカル開発用（emulator）
const SEND_EMAIL_URL =
  // "http://127.0.0.1:5001/raising2024-7e234/us-central1/sendEmail";

  // ★本番用に切り替える時は以下に変更
  "https://us-central1-raising2024-7e234.cloudfunctions.net/sendEmail";

const getTodayEmailCount = async () => {
  try {
    const db = await getDb();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const emailRecordsRef = collection(db, "emailRecords");
    const q = query(
      emailRecordsRef,
      where("timestamp", ">=", Timestamp.fromDate(today)),
      where("timestamp", "<", Timestamp.fromDate(tomorrow))
    );

    const snapshot = await getDocs(q);
    console.log(`📊 今日の送信済みメール数: ${snapshot.size} 通`);
    return snapshot.size;
  } catch (error) {
    console.warn("⚠️ メール数カウント失敗 → 制限超過扱い", error);
    return DAILY_EMAIL_LIMIT + 1;
  }
};

export const EmailSenderBase = async (
  selectedEmail,
  subject,
  text,
  setResponseMessage = null
) => {
  console.log("========================================");
  console.log("🚀 EmailSenderBase が呼び出されました");
  console.log("呼び出し元情報:", {
    selectedEmail,
    subject,
    text: text.substring(0, 100) + (text.length > 100 ? "..." : ""),
  });
  console.log("========================================");

  try {
    // メールアドレス正規化（詳細ログ付き）
    const emailsArray = Array.isArray(selectedEmail)
      ? selectedEmail
      : typeof selectedEmail === "string" && selectedEmail.trim()
      ? [selectedEmail]
      : [];

    console.log("📧 元の宛先データ:", emailsArray);

    const flatEmails = emailsArray
      .flat(Infinity)
      .map((v) => (v || "").toString().trim())
      .filter((email) => email.length > 0 && email.includes("@"));

    console.log("📧 正規化後の宛先リスト:", flatEmails);

    if (flatEmails.length === 0) {
      throw new Error("有効なメールアドレスがありません");
    }

    // 送信制限チェック
    const count = await getTodayEmailCount();
    if (count > DAILY_EMAIL_LIMIT) {
      throw new Error("本日のメール送信上限に達しています");
    }

    // ★ここが最も重要！実際にFunctionsに送られるデータ
    const payload = {
      to: flatEmails, // 配列形式で送信
      subject,
      text,
    };

    console.log("📤 【Cloud Functions に送信される payload】");
    console.log(JSON.stringify(payload, null, 2));
    console.log("送信先URL:", SEND_EMAIL_URL);

    // fetch実行
    const response = await fetch(SEND_EMAIL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // 認証トークンも表示（ある場合）
        // Authorization: await getAuth().currentUser?.getIdToken() || "なし",
      },
      body: JSON.stringify(payload),
    });

    // レスポンスの生データを取得
    const responseText = await response.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch {
      data = responseText;
    }

    console.log("📥 【Cloud Functions からの応答】");
    console.log("ステータス:", response.status, response.statusText);
    console.log("応答本文:", data);

    if (!response.ok) {
      throw new Error(data.error || data.message || "送信失敗");
    }

    // Firestore記録
    try {
      const db = await getDb();
      await addDoc(collection(db, "emailRecords"), {
        subject,
        content: text,
        recipients: flatEmails,
        timestamp: Timestamp.now(),
        from: "EmailSenderBase", // どこから送られたか識別用
      });
      console.log("✅ Firestore に送信記録を保存しました");
    } catch (recordError) {
      console.warn("⚠️ 送信記録保存失敗（メールは送信済み）", recordError);
    }

    const successMsg =
      typeof data === "object" ? data.message : data || "送信成功";
    console.log("✅ メール送信成功:", successMsg);
    if (setResponseMessage) setResponseMessage(successMsg);

    return data;
  } catch (error) {
    console.error("❌ EmailSenderBase でエラー発生:", error);
    const msg = error.message || "メール送信に失敗しました";
    if (setResponseMessage) setResponseMessage(msg);
    throw error;
  }
};
